<script setup>
/**
 * "Allow Claude to read your setup and draft changes?" (services spec §8 P-8.4). The connector's
 * OAuth authorize step lands here, inside her own Chatwoot session: she never sees or pastes a key.
 *
 * Allow asks the BFF for the code and sends her straight back to Claude; Cancel sends her back
 * with `access_denied`. With several workspaces the BFF adds `pick=1` and she chooses the one
 * Claude may draft for. The list of what it can and cannot do is the binding consent copy: it
 * names the customer chats it reads, and that it never spends her Waslo credits.
 */
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import WasloPageLayout from '../components/WasloPageLayout.vue';
import WasloSelect from '../components/WasloSelect.vue';
import { useWasloI18n } from '../i18n.js';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';
import { wasloApi, WASLO_ERROR_CODES } from '../api/client.js';
import { adminAccounts, leaveTo } from '../helpers/claude.js';

const { t } = useWasloI18n();
const route = useRoute();
const router = useRouter();
const accountId = useWasloAccountId();
// Not `useStore()`: it throws with no store installed (`useChatOwnership`'s escape hatch).
const store = getCurrentInstance()?.proxy?.$store ?? null;

const requestId = computed(() =>
  typeof route?.query?.request === 'string' ? route.query.request : ''
);
const accounts = computed(() => adminAccounts(store?.getters?.getCurrentUser));
const showPicker = computed(
  () => route?.query?.pick === '1' && accounts.value.length > 1
);
const accountName = computed(
  () =>
    accounts.value.find(a => a.id === accountId.value)?.name ??
    String(accountId.value ?? '')
);
const accountOptions = computed(() =>
  accounts.value.map(a => ({ value: String(a.id), label: a.name }))
);

const request = ref(null);
const loading = ref(true);
const expired = ref(false);
const failure = ref('');
const busy = ref(false);
const leaving = ref(false);

const load = async () => {
  loading.value = true;
  expired.value = false;
  failure.value = '';
  try {
    if (accountId.value === null || requestId.value === '') {
      expired.value = true;
      return;
    }
    request.value = await wasloApi.getConnectRequest(
      accountId.value,
      requestId.value
    );
  } catch (error) {
    if (error?.code === WASLO_ERROR_CODES.NOT_FOUND) expired.value = true;
    else failure.value = t('WASLO.CLAUDE.CONNECT.FAILED');
  } finally {
    loading.value = false;
  }
};

onMounted(load);

const switchAccount = value => {
  router.push({
    name: 'waslo_connect_ai',
    params: { accountId: value },
    query: { request: requestId.value, pick: '1' },
  });
};

const decide = async allow => {
  if (busy.value || leaving.value || accountId.value === null) return;
  busy.value = true;
  failure.value = '';
  try {
    const redirect = await wasloApi.decideConnectRequest(
      accountId.value,
      requestId.value,
      allow
    );
    leaving.value = true;
    leaveTo(redirect);
  } catch (error) {
    if (error?.code === WASLO_ERROR_CODES.NOT_FOUND) expired.value = true;
    else failure.value = t('WASLO.CLAUDE.CONNECT.FAILED');
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <WasloPageLayout
    :header-title="t('WASLO.CLAUDE.CONNECT.TITLE')"
    :is-fetching="loading"
  >
    <template #body>
      <div class="flex flex-col gap-6 pb-10 max-w-[62ch]" data-waslo-connect-ai>
        <Banner v-if="expired" color="slate" data-waslo-connect-expired>
          {{ t('WASLO.CLAUDE.CONNECT.EXPIRED') }}
        </Banner>

        <template v-else-if="request">
          <div class="flex flex-col gap-2">
            <h2
              class="mb-0 text-lg font-medium text-n-slate-12"
              data-waslo-connect-ask
            >
              {{
                t('WASLO.CLAUDE.CONNECT.ASK', {
                  app: request.clientName,
                  account: accountName,
                })
              }}
            </h2>
            <p
              class="mb-0 text-sm text-n-slate-11"
              dir="auto"
              data-waslo-connect-host
            >
              {{
                t('WASLO.CLAUDE.CONNECT.RETURNS_TO', {
                  host: request.redirectHost,
                })
              }}
            </p>
          </div>

          <WasloSelect
            v-if="showPicker"
            :model-value="String(accountId)"
            :label="t('WASLO.CLAUDE.CONNECT.ACCOUNT')"
            :options="accountOptions"
            data-waslo-connect-account
            @update:model-value="switchAccount"
          />

          <section class="flex flex-col gap-2" data-waslo-connect-can>
            <h3 class="mb-0 text-sm font-medium text-n-slate-12">
              {{ t('WASLO.CLAUDE.CONNECT.CAN') }}
            </h3>
            <ul
              class="flex flex-col gap-1 mb-0 text-sm list-disc ps-5 text-n-slate-11"
            >
              <li>{{ t('WASLO.CLAUDE.CONNECT.CAN_READ') }}</li>
              <li>{{ t('WASLO.CLAUDE.CONNECT.CAN_CHATS') }}</li>
              <li>{{ t('WASLO.CLAUDE.CONNECT.CAN_DRAFT') }}</li>
            </ul>
          </section>

          <section class="flex flex-col gap-2" data-waslo-connect-cannot>
            <h3 class="mb-0 text-sm font-medium text-n-slate-12">
              {{ t('WASLO.CLAUDE.CONNECT.CANNOT') }}
            </h3>
            <ul
              class="flex flex-col gap-1 mb-0 text-sm list-disc ps-5 text-n-slate-11"
            >
              <li>{{ t('WASLO.CLAUDE.CONNECT.CANNOT_SWITCH') }}</li>
              <li>{{ t('WASLO.CLAUDE.CONNECT.CANNOT_SEND') }}</li>
              <li>{{ t('WASLO.CLAUDE.CONNECT.CANNOT_SECRETS') }}</li>
              <li>{{ t('WASLO.CLAUDE.CONNECT.CANNOT_REMOVE') }}</li>
            </ul>
          </section>

          <p class="mb-0 text-sm text-n-slate-12" data-waslo-connect-credits>
            {{ t('WASLO.CLAUDE.CONNECT.CREDITS') }}
          </p>

          <div class="flex flex-wrap gap-2">
            <Button
              :label="t('WASLO.CLAUDE.CONNECT.ALLOW')"
              :disabled="busy || leaving"
              data-waslo-connect-allow
              @click="decide(true)"
            />
            <Button
              :label="t('WASLO.CLAUDE.CONNECT.CANCEL')"
              variant="faded"
              color="slate"
              :disabled="busy || leaving"
              data-waslo-connect-cancel
              @click="decide(false)"
            />
          </div>
          <p
            v-if="leaving"
            class="mb-0 text-sm text-n-slate-11"
            data-waslo-connect-leaving
          >
            {{ t('WASLO.CLAUDE.CONNECT.LEAVING') }}
          </p>
        </template>

        <p
          v-if="failure"
          class="mb-0 text-sm text-n-ruby-11"
          data-waslo-connect-failure
        >
          {{ failure }}
        </p>
      </div>
    </template>
  </WasloPageLayout>
</template>
